// useUndoHistory — the editor's Undo and Redo, saved (the real-time editor
// plan, PR2). The history is per material (the host clears it on a switch)
// and holds at most HISTORY_LIMIT steps. Undo and Redo show the step at once
// and queue its commands (undoSteps.ts decides them) behind every earlier
// save, like any edit. A step whose blocks changed elsewhere is refused,
// here or by the server: what is shown goes back, the history is cleared and
// the notice says so. Nothing is overwritten.

export type { UndoHistory };
export { useUndoHistory };

import { useState } from "react";
import type { ComposeLoop } from "../../app/composeLoop.js";
import type { HostNotice, ResourceEdited } from "../../app/frame.js";
import type { Element } from "../Editor/types.js";
import { childIdsOf } from "./edits.js";
import type { SaveEvent } from "./saveQueue.js";
import { type EditPorts, sendOps } from "./sendOps.js";
import type { Snapshot } from "./types.js";
import { HISTORY_LIMIT, type HistoryStep, planStep } from "./undoSteps.js";
import type { StructuralRun } from "./useSaveQueue.js";

type History = {
    readonly past: readonly HistoryStep[];
    readonly future: readonly HistoryStep[];
};

const emptyHistory: History = { past: [], future: [] };

type UndoHistory = {
    readonly canUndo: boolean;
    readonly canRedo: boolean;
    // An edit was made (shown already): it becomes the newest step and
    // forks the redo stack.
    readonly record: (before: Snapshot, after: Snapshot) => void;
    // The newest step's own save was refused (the edit is taken back).
    readonly dropNewest: () => void;
    readonly clear: () => void;
    readonly undo: () => void;
    readonly redo: () => void;
};

const useUndoHistory = ({
    ports,
    activeMaterialId,
    isShown,
    elements,
    setElements,
    setLoop,
    queue,
    dispatch,
    showNotice,
}: {
    readonly ports: EditPorts;
    readonly activeMaterialId: string | null;
    // Whether `materialId` is still the one shown (a refusal lands later).
    readonly isShown: (materialId: string) => boolean;
    readonly elements: readonly Element[] | null;
    readonly setElements: (elements: readonly Element[]) => void;
    readonly setLoop: (loop: ComposeLoop) => void;
    readonly queue: (event: SaveEvent<StructuralRun>) => void;
    readonly dispatch: (transition: ResourceEdited) => void;
    readonly showNotice: (n: HostNotice) => void;
}): UndoHistory => {
    const [history, setHistory] = useState<History>(emptyHistory);

    // Take a step from one snapshot to the other, and move it to the other
    // stack. `refused` names the notice a refusal shows.
    const travel = (
        from: Snapshot,
        to: Snapshot,
        moved: History,
        refused: HostNotice,
    ) => {
        if (activeMaterialId === null || elements === null) return;
        const plan = planStep(elements, from.elements, to.elements);
        if (plan.type === "changed-elsewhere") {
            setHistory(emptyHistory);
            showNotice(refused);
            return;
        }
        const materialId = activeMaterialId;
        const shownBefore = elements;
        setElements(plan.shown);
        setLoop(to.loop);
        setHistory(moved);
        if (plan.ops.length === 0) return;
        dispatch({ type: "resource-edited", id: materialId });
        queue({
            type: "committed",
            materialId,
            save: {
                type: "structural",
                mints: plan.mints,
                within: childIdsOf(plan.shown, plan.mints),
                run: {
                    persist: sendOps(ports, plan.ops),
                    refused: (error) => {
                        if (!isShown(materialId)) return;
                        setElements(shownBefore);
                        setLoop(from.loop);
                        setHistory(emptyHistory);
                        showNotice(
                            error._tag === "External.Refused" ||
                                error._tag === "External.Rejected"
                                ? refused
                                : { type: "edit-failed" },
                        );
                    },
                },
            },
        });
    };

    const undo = () => {
        const step = history.past.at(-1);
        if (step === undefined) return;
        travel(
            step.after,
            step.before,
            {
                past: history.past.slice(0, -1),
                future: [...history.future, step],
            },
            { type: "undo-refused" },
        );
    };
    const redo = () => {
        const step = history.future.at(-1);
        if (step === undefined) return;
        travel(
            step.before,
            step.after,
            {
                past: [...history.past, step],
                future: history.future.slice(0, -1),
            },
            { type: "redo-refused" },
        );
    };

    return {
        canUndo: history.past.length > 0,
        canRedo: history.future.length > 0,
        record: (before, after) =>
            setHistory((h) => ({
                past: [...h.past, { before, after }].slice(-HISTORY_LIMIT),
                future: [],
            })),
        dropNewest: () =>
            setHistory((h) => ({ ...h, past: h.past.slice(0, -1) })),
        clear: () => setHistory(emptyHistory),
        undo,
        redo,
    };
};
